import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";

export type BeatPlayer = {
  playingKey: string | null;
  muted: boolean;
  toggle: (key: string, url: string) => void;
  toggleMute: () => void;
  // Fills buf with the playing preview's frequency levels. False when there
  // is nothing to read: no graph, or the host sent no CORS header and the
  // preview is playing through the plain element.
  readLevels: (buf: Uint8Array<ArrayBuffer>) => boolean;
  elements: ReactElement;
};

type AudioContextCtor = typeof AudioContext;

// useBeatPlayer plays one beat preview at a time, and only ever from a
// click. Two elements back it: `graphed` is cross-origin and runs through an
// AnalyserNode for the bar visualiser; `plain` has no crossorigin attribute and
// is the fallback when the audio host sends no CORS header, since a
// crossorigin element without one refuses to load at all.
export function useBeatPlayer(): BeatPlayer {
  const graphed = useRef<HTMLAudioElement>(null);
  const plain = useRef<HTMLAudioElement>(null);
  const ctx = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const corsBlocked = useRef(new Set<string>());
  const current = useRef<{ key: string; url: string } | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);

  const ensureGraph = useCallback(() => {
    if (ctx.current || !graphed.current) return;
    const Ctor: AudioContextCtor | undefined =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
    if (!Ctor) return;
    try {
      const audioCtx = new Ctor();
      const source = audioCtx.createMediaElementSource(graphed.current);
      const node = audioCtx.createAnalyser();
      node.fftSize = 256;
      source.connect(node);
      node.connect(audioCtx.destination);
      ctx.current = audioCtx;
      analyser.current = node;
    } catch {
      // No graph: the element still plays, the visualiser stays static.
    }
  }, []);

  const silenceAll = useCallback(() => {
    graphed.current?.pause();
    plain.current?.pause();
    void ctx.current?.suspend().catch(() => undefined);
  }, []);

  const stop = useCallback(() => {
    current.current = null;
    silenceAll();
    setPlayingKey(null);
  }, [silenceAll]);

  const playPlain = useCallback((key: string, url: string) => {
    const el = plain.current;
    if (!el) return;
    el.src = url;
    el.play().catch((err: DOMException) => {
      if (err.name !== "AbortError" && current.current?.key === key) {
        current.current = null;
        setPlayingKey(null);
      }
    });
  }, []);

  const play = useCallback(
    (key: string, url: string) => {
      silenceAll();
      current.current = { key, url };
      setMuted(false);
      setPlayingKey(key);

      if (corsBlocked.current.has(url)) {
        playPlain(key, url);
        return;
      }
      ensureGraph();
      const el = graphed.current;
      if (!el) return;
      void ctx.current?.resume().catch(() => undefined);
      el.src = url;
      el.play().catch((err: DOMException) => {
          if (err.name === "AbortError" || current.current?.key !== key) return;
          if (err.name === "NotAllowedError") {
            // The browser refused playback: reset the button, never leave it
            // showing a beat that is not playing.
            current.current = null;
            setPlayingKey(null);
          }
          // Load failures arrive through onError below.
        });
    },
    [ensureGraph, playPlain, silenceAll],
  );

  const onGraphedError = useCallback(() => {
    const now = current.current;
    const el = graphed.current;
    if (!now || !el || el.getAttribute("src") !== now.url) return;
    corsBlocked.current.add(now.url);
    el.removeAttribute("src");
    playPlain(now.key, now.url);
  }, [playPlain]);

  const onEnded = useCallback(() => stop(), [stop]);

  const toggle = useCallback(
    (key: string, url: string) => {
      if (current.current?.key === key) stop();
      else play(key, url);
    },
    [play, stop],
  );

  const toggleMute = useCallback(() => {
    if (muted) {
      setMuted(false);
      return;
    }
    setMuted(true);
    stop();
  }, [muted, stop]);

  const readLevels = useCallback((buf: Uint8Array<ArrayBuffer>) => {
    const node = analyser.current;
    const now = current.current;
    if (!node || !now || corsBlocked.current.has(now.url)) return false;
    node.getByteFrequencyData(buf);
    return true;
  }, []);

  useEffect(() => () => {
    void ctx.current?.close().catch(() => undefined);
  }, []);

  const elements = (
    <div hidden>
      <audio ref={graphed} crossOrigin="anonymous" preload="none" loop onError={onGraphedError} onEnded={onEnded} />
      <audio ref={plain} preload="none" loop onEnded={onEnded} />
    </div>
  );

  return { playingKey, muted, toggle, toggleMute, readLevels, elements };
}
